import i18n from '../i18n';
import { ORDER_STATUS_LABELS } from '../constants';
import { formatLocalizedDate, formatLocalizedPrice, getActiveLocale } from '../i18n/localeUtils';

// Date formatting - Firestore Timestamp ve Date nesnelerini destekler
export const formatDate = (dateValue) => {
  if (!dateValue) return '-';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '-';
  }

  return formatLocalizedDate(date, i18n.language);
};

// Tarih değerini input[type="date"] için YYYY-MM-DD formatına çevirir
export const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';

  let date;

  // Firestore Timestamp nesnesi mi kontrol et
  if (dateValue && typeof dateValue.toDate === 'function') {
    date = dateValue.toDate();
  } else if (dateValue instanceof Date) {
    date = dateValue;
  } else {
    date = new Date(dateValue);
  }

  // Geçersiz tarih kontrolü
  if (isNaN(date.getTime())) {
    return '';
  }

  // YYYY-MM-DD formatına çevir
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

// Price formatting - Türk para birimi formatı
export const formatPrice = (price) => {
  const num = parseFloat(price || 0);

  // Türk para birimi formatı: 1.234,56
  return formatLocalizedPrice(price, i18n.language);
};

// Validation functions
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const validatePhone = (phone) => {
  const phoneRegex = /^[0-9\s\-+()]{10,}$/;
  return phoneRegex.test(phone);
};

// Local storage helpers
export const setLocalStorage = (key, value) => {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.error('Local storage error:', error);
  }
};

export const getLocalStorage = (key) => {
  try {
    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  } catch (error) {
    console.error('Local storage error:', error);
    return null;
  }
};

// Error handling
export const handleFirebaseError = (error) => {
  console.error('Firebase error:', error);

  switch (error.code) {
    case 'auth/user-not-found':
      return i18n.t('errors.auth.userNotFound');
    case 'auth/wrong-password':
      return i18n.t('errors.auth.wrongPassword');
    case 'auth/invalid-email':
      return i18n.t('errors.auth.invalidEmail');
    case 'auth/too-many-requests':
      return i18n.t('errors.auth.tooManyRequests');
    case 'permission-denied':
      return i18n.t('errors.auth.permissionDenied');
    case 'unavailable':
      return i18n.t('errors.auth.unavailable');
    default:
      return i18n.t('errors.auth.default');
  }
};

export { getActiveLocale, formatLocalizedDate, formatLocalizedPrice };

// Loading states
export const createLoadingState = () => ({
  isLoading: false,
  error: null,
  data: null
});

// Debounce function
export const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Tül Hesaplama Fonksiyonları
export const calculatePileDensity = (pileModel) => {
  const densityValues = {
    'Standart SEYREK': 2,
    'Standart ORTA': 2.5,
    'Standart SIK': 3,
    'S Pile': 3,
    'Amerikan Pile 2\'li': 3,
    'Amerikan Pile 3\'lü': 4,
    'Kapsül Pile': 3
  };

  return densityValues[pileModel] || 0;
};

export const calculateUsedM2 = (width, height, pileModel, customPileDensity = null) => {
  // Eğer custom pile sıklığı verilmişse onu kullan, yoksa pile modelinden hesapla
  let pileDensity = customPileDensity !== null ? parseFloat(customPileDensity) : calculatePileDensity(pileModel);

  // CM değerlerini metre'ye çevir (100'e böl)
  const widthNum = (parseFloat(width) || 0) / 100;
  const heightNum = (parseFloat(height) || 0) / 100;

  if (!pileDensity || pileDensity === 0) {
    // Pile sıklığı olmayan modeller için
    switch (pileModel) {
      case 'Çima Dikiş':
        return widthNum + 0.75;
      case 'Düz Dikiş':
        return widthNum + 0.5;
      case 'Kumaş Güneşlik':
        return widthNum + 0.2;
      default:
        return widthNum;
    }
  }

  // Pile sıklığı olan modeller için
  return (widthNum * pileDensity) + 0.5;
};

export const calculateCapsulePrice = (usedM2, capsuleUnitPrice) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const capsuleUnitPriceNum = parseFloat(capsuleUnitPrice) || 0;

  // Metrekareyi bir üst tam sayıya yuvarla
  const roundedM2 = Math.ceil(usedM2Num);

  // Metrekarede 7 kapsül kullanılıyor
  const capsuleCount = roundedM2 * 7;

  return capsuleCount * capsuleUnitPriceNum;
};

export const calculateTotalPrice = (usedM2, unitPrice, extraPrice, pileModel, capsulePrice = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const extraPriceNum = parseFloat(extraPrice) || 0;
  const capsulePriceNum = parseFloat(capsulePrice) || 0;

  // Standart modeller - ek ücret varsa ekle
  if (['Standart SEYREK', 'Standart ORTA', 'Standart SIK'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Dikiş modelleri - ek ücret varsa ekle
  if (['Çima Dikiş', 'Düz Dikiş', 'Kumaş Güneşlik'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Ek ücretli modeller
  if (['S Pile', 'Amerikan Pile 2\'li', 'Amerikan Pile 3\'lü'].includes(pileModel)) {
    return usedM2Num * (unitPriceNum + extraPriceNum);
  }

  // Kapsül Pile - her zaman kapsül ücreti ekle (ek ücret boş olsa bile)
  if (pileModel === 'Kapsül Pile') {
    const basePrice = usedM2Num * (unitPriceNum + extraPriceNum);
    return basePrice + capsulePriceNum;
  }

  return 0;
};

export const calculateTotalCost = (usedM2, unitCost) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitCostNum = parseFloat(unitCost) || 0;
  return usedM2Num * unitCostNum;
};

/** Perakende Kanat astar: kullanılan metre × birim fiyat/maliyet (adet ile çarpılır) */
export const calculateKanatAstarAmounts = (usedM2, astarUnitPrice, astarUnitCost, quantity = 1) => {
  const meters = parseFloat(usedM2) || 0;
  const price = parseFloat(astarUnitPrice) || 0;
  const cost = parseFloat(astarUnitCost) || 0;
  const qty = parseFloat(quantity) || 1;
  const feePerPiece = meters * price;
  const costPerPiece = meters * cost;
  return {
    feePerPiece,
    costPerPiece,
    totalFee: feePerPiece * qty,
    totalCost: costPerPiece * qty,
    totalFeeFormatted: (feePerPiece * qty).toFixed(2),
    costPerPieceFormatted: costPerPiece.toFixed(2),
    feePerPieceFormatted: feePerPiece.toFixed(2)
  };
};

export const calculateDiscountedPrice = (totalPrice, discount) => {
  const totalPriceNum = parseFloat(totalPrice) || 0;
  const discountNum = parseFloat(discount) || 0;

  if (discountNum <= 0) return totalPriceNum;

  const discountAmount = totalPriceNum * (discountNum / 100);
  return totalPriceNum - discountAmount;
};

// Stor ve Zebra Hesaplama Fonksiyonları
export const calculateStorZebraUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için özel kurallar
  if (heightNum <= 2) {
    heightNum = 2;
  } else if (heightNum > 2 && heightNum <= 3) {
    heightNum = 3;
  } else if (heightNum > 3 && heightNum <= 4) {
    heightNum = 4;
  } else if (heightNum > 4) {
    heightNum = 7;
  }

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateStorZebraTotalPrice = (usedM2, unitPrice, width, extras = {}, motorPriceTL = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;

  // Ekstra seçenekler için özel en hesaplaması
  let widthForExtras = (parseFloat(width) || 0) / 100; // cm -> metre
  if (widthForExtras > 0) {
    if (widthForExtras < 1) widthForExtras = 1;
    // 10'un katına (0.1m) yukarı yuvarla
    widthForExtras = Math.ceil(widthForExtras * 10) / 10;
  }

  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  // Ekstra ücretleri hesaplanan en (widthForExtras) ile çarpıyoruz
  const extraCostsTotal = Object.values(extras).reduce((sum, val) => {
    const valNum = parseFloat(val) || 0;
    return sum + (valNum * widthForExtras);
  }, 0);

  return basePrice + extraCostsTotal + motorPriceTLNum + mountingNum;
};

// Ahşap Jaluzi ve Metal Jaluzi Hesaplama Fonksiyonları
export const calculateJaluziUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // En için minimum 1 metre kontrolü
  if (widthNum < 1) {
    widthNum = 1;
  }

  // Boy için minimum 1 metre kontrolü
  if (heightNum < 1) {
    heightNum = 1;
  }

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  return widthNum * heightNum;
};

export const calculateJaluziTotalPrice = (usedM2, unitPrice, mounting = 0, motorPriceTL = 0, zigilAmount = 0, ikiCamArasiAmount = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;
  const zigilAmountNum = parseFloat(zigilAmount) || 0;
  const ikiCamArasiAmountNum = parseFloat(ikiCamArasiAmount) || 0;

  const basePrice = usedM2Num * unitPriceNum;

  return basePrice + mountingNum + motorPriceTLNum + zigilAmountNum + ikiCamArasiAmountNum;
};

// Dikey Perde Hesaplama Fonksiyonu
export const calculateDikeyPerdeTotalPrice = (usedM2, unitPrice, backgroundCurtain = 0, mounting = 0, motorPriceTL = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPriceNum = parseFloat(unitPrice) || 0;
  const backgroundCurtainNum = parseFloat(backgroundCurtain) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceTLNum = parseFloat(motorPriceTL) || 0;

  const basePrice = usedM2Num * (unitPriceNum + backgroundCurtainNum);

  return basePrice + mountingNum + motorPriceTLNum;
};

// Plise Hesaplama Fonksiyonu
export const calculatePliseUsedM2 = (width, height) => {
  // CM değerlerini metre'ye çevir (100'e böl)
  let widthNum = (parseFloat(width) || 0) / 100;
  let heightNum = (parseFloat(height) || 0) / 100;

  // 10'un katına yuvarlama
  widthNum = Math.ceil(widthNum * 10) / 10;
  heightNum = Math.ceil(heightNum * 10) / 10;

  // İlk çarpım hesaplama
  const calculatedArea = widthNum * heightNum;

  // Eğer çarpım 1'den büyükse, değerleri aynen kullan
  // Eğer çarpım 1'den küçükse, direkt 1 olarak ayarla
  if (calculatedArea >= 1) {
    return calculatedArea;
  } else {
    return 1;
  }
};

export const calculatePliseTotalPrice = (usedM2, unitPrice1 = 0, unitPrice2 = 0, mounting = 0) => {
  const usedM2Num = parseFloat(usedM2) || 0;
  const unitPrice1Num = parseFloat(unitPrice1) || 0;
  const unitPrice2Num = parseFloat(unitPrice2) || 0;
  const mountingNum = parseFloat(mounting) || 0;

  // Toplam birim fiyat (iki ürünün toplamı)
  const totalUnitPrice = unitPrice1Num + unitPrice2Num;

  // Eğer m2 0 ise (ölçü girilmemişse) varsayılan 1 m2 üzerinden hesapla 
  // (Ancak birim fiyatlar 0 ise sonuç yine 0/montaj olacaktır)
  const effectiveM2 = usedM2Num > 0 ? usedM2Num : 1;

  const basePrice = effectiveM2 * totalUnitPrice;

  return basePrice + mountingNum;
};

// Aksesuar Hesaplama Fonksiyonu
export const calculateAksesuarTotalPrice = (unitPrice, quantity, meter = 0) => {
  const price = parseFloat(unitPrice) || 0;
  const qty = parseFloat(quantity) || 0;
  const m = parseFloat(meter) || 0;

  if (m > 0) {
    return price * qty * m;
  }
  return price * qty;
};

// Katlamalı Perde Hesaplama Fonksiyonları
export const calculateKatlamaliPerdeUsedM = (width) => {
  // CM değerini metre'ye çevir ve 0.35 ekle
  const widthNum = (parseFloat(width) || 0) / 100;
  return widthNum + 0.35;
};

export const calculateKatlamaliPerdeTotalPrice = (usedM, fabricPrice = 0, mechanismPrice = 0, accessories = [], mounting = 0, motorPrice = 0) => {
  const usedMNum = parseFloat(usedM) || 0;
  const fabricPriceNum = parseFloat(fabricPrice) || 0;
  const mechanismPriceNum = parseFloat(mechanismPrice) || 0;
  const mountingNum = parseFloat(mounting) || 0;
  const motorPriceNum = parseFloat(motorPrice) || 0;

  // Aksesuar toplamını hesapla (dizi ise)
  let totalAccessoryPrice = 0;
  if (Array.isArray(accessories)) {
    totalAccessoryPrice = accessories.reduce((sum, acc) => sum + (parseFloat(acc.total) || 0), 0);
  } else {
    totalAccessoryPrice = parseFloat(accessories) || 0;
  }

  // Toplam Tutar = (Kullanılan metre x (Kumaş + Mekanizma)) + Aksesuarlar + Montaj + Motor
  const basePrice = usedMNum * (fabricPriceNum + mechanismPriceNum);

  return basePrice + totalAccessoryPrice + mountingNum + motorPriceNum;
};

// Dashboard İstatistik Hesaplama Fonksiyonları
export const calculateOrderStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: orders.length,
    today: 0,
    thisWeek: 0,
    thisMonth: 0
  };

  orders.forEach(order => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = order.orderDate || order.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);

    if (createdAt >= today) {
      stats.today++;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek++;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth++;
    }
  });

  return stats;
};

export const calculateRevenueStats = (orders) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const thisWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const thisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const stats = {
    total: 0,
    today: 0,
    thisWeek: 0,
    thisMonth: 0,
    average: 0
  };

  orders.forEach(order => {
    // İndirimli tutar varsa onu kullan, yoksa toplam tutarı kullan
    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    stats.total += orderAmount;

    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);

    if (createdAt >= today) {
      stats.today += orderAmount;
    }
    if (createdAt >= thisWeek) {
      stats.thisWeek += orderAmount;
    }
    if (createdAt >= thisMonth) {
      stats.thisMonth += orderAmount;
    }
  });

  stats.average = orders.length > 0 ? stats.total / orders.length : 0;

  return stats;
};

export const calculateProductTypeDistribution = (orders) => {
  const distribution = {};

  orders.forEach(order => {
    const productType = order.productType || 'unknown';
    distribution[productType] = (distribution[productType] || 0) + 1;
  });

  return distribution;
};

export const calculateOrderStatusDistribution = (customers) => {
  const distribution = {};

  // ORDER_STATUS_LABELS'dan tüm durumları al
  Object.keys(ORDER_STATUS_LABELS).forEach((status) => {
    const statusLabel = i18n.t(`constants.orderStatus.${status}`, {
      defaultValue: ORDER_STATUS_LABELS[status]
    });
    distribution[statusLabel] = 0;
  });

  customers.forEach(customer => {
    const status = customer.orderStatus || 'new_order';
    const statusLabel = i18n.t(`constants.orderStatus.${status}`, {
      defaultValue: ORDER_STATUS_LABELS[status] || ORDER_STATUS_LABELS.new_order
    });
    distribution[statusLabel] = (distribution[statusLabel] || 0) + 1;
  });

  return distribution;
};

export const calculateMonthlyCustomerGrowth = (customers) => {
  const monthlyData = {};

  customers.forEach(customer => {
    // Sipariş tarihi varsa onu kullan, yoksa createdAt kullan
    const dateToUse = customer.orderDate || customer.createdAt;
    const createdAt = dateToUse?.toDate() || new Date(dateToUse);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + 1;
  });

  return monthlyData;
};

export const calculateMonthlyRevenue = (orders) => {
  const monthlyData = {};

  orders.forEach(order => {
    // Siparişler için createdAt kullan
    const createdAt = order.createdAt?.toDate() || new Date(order.createdAt);
    const monthKey = `${createdAt.getFullYear()}-${String(createdAt.getMonth() + 1).padStart(2, '0')}`;

    const orderAmount = order.discountedPrice && parseFloat(order.discount) > 0
      ? parseFloat(order.discountedPrice || 0)
      : parseFloat(order.totalPrice || 0);

    monthlyData[monthKey] = (monthlyData[monthKey] || 0) + orderAmount;
  });

  return monthlyData;
};

export {
  fetchExchangeRates,
  invalidateExchangeRatesCache,
  refreshExchangeRates,
  setManualExchangeRate,
  clearManualExchangeRate,
  getManualExchangeOverrides,
  isManualExchangeRate,
  subscribeExchangeRates,
  formatExchangeRatesForDisplay,
  convertForeignToLocal,
  isLocalCurrency,
  DEFAULT_EXCHANGE_RATES,
  parseExchangeRatesFromApi,
  getUsdToKztRate
} from './exchangeRates';
export {
  isProductPriceInUsd,
  isMechanismPriceInUsd,
  convertUsdAmountToKzt,
  toKztPrice,
  convertProductToKztForOrder,
  resolveJaluziOrderPricing,
  convertMechanismToKztForOrder,
  formatStoredPriceAsKzt,
  formatUsdKztHint,
  formatProductListPrice,
  PRODUCT_PRICE_CURRENCY_USD
} from './productPriceUtils';
export { resolveOrderTotalCost, resolveOrderUnitCost, hasExplicitHiddenUnitCost } from './orderCostUtils';
export { sanitizePhoneInput } from './phoneInputUtils';